import indeedLogo from './IndeedLogo.svg';
import successfulSignInImage from './SuccessfulSignInImage.png';
import { Box, Pressable, Text, type TextRef } from '../../../../../../mads';

import {
  AvroOptional,
  log as logReceivedFlag,
} from '@indeed/signals-schema/com/indeed/signals/schema/profile/MobRcvFlag';
import { useCallback, useEffect, useRef, type FC } from 'react';
import { Image, Modal, StyleSheet } from 'react-native';

const logModalDisplayed = (mobtk: string, loggingPrefix: string) => {
  logReceivedFlag({
    mobtk,
    parentEventType: 'mobviewjob',
    flag: AvroOptional.string(`${loggingPrefix}_MODAL_SEEN`),
    ts: AvroOptional.long(new Date().getTime()),
  });
};

const logContinueClicked = (mobtk: string, loggingSrc: string) => {
  logReceivedFlag({
    mobtk,
    parentEventType: 'mobviewjob',
    flag: AvroOptional.string(`${loggingSrc}_CONTINUE_CLICKED`),
    ts: AvroOptional.long(new Date().getTime()),
  });
};

const LOGGING_PREFIX = 'SUCCESSFUL_SIGNIN';
const LOGGING_SRC = 'MOBILE_WEB';

export interface SuccessfulSignInModalProps {
  isOpen: boolean;
  onClose?: () => void;
  titleText?: string;
  successfulSigninText?: string;
  continueButtonText?: string;
  thirdPartyUrl: string | null;
  mobtk?: string;
}

export const SuccessfulSignInModal: FC<SuccessfulSignInModalProps> = ({
  isOpen,
  onClose,
  titleText = "Success!",
  successfulSigninText = "You've signed in to your account. Good luck with your application.",
  continueButtonText = 'Continue to application',
  thirdPartyUrl,
  mobtk,
}) => {
  const titleRef = useRef<TextRef>(null);
  useEffect(() => {
    if (isOpen) {
      if (mobtk) {
        logModalDisplayed(mobtk, LOGGING_PREFIX);
      }
      // Focus title for accessibility
      titleRef.current?.focus?.();
    }
  }, [isOpen, mobtk]);

  const handleRedirectToThirdPartySite = useCallback(() => {
    if (mobtk) {
      logContinueClicked(mobtk, LOGGING_SRC);
    }

    // Log apply-authed-redirect
    if (mobtk) {
      const thirdPartyApplyLogUrl = new URL(
        '/m/rpc/log/apply-authed-redirect',
        window.location.origin,
      );
      thirdPartyApplyLogUrl.searchParams.set('tk', mobtk);
      void fetch(thirdPartyApplyLogUrl.toString());
    }

    if (thirdPartyUrl) {
      // Append log=2 to mark this as the post-passport-sign-in completion of an
      // intercepted apply (distinguishes from a normal /applystart redirect, which
      // has no log param, and from the at-gate-time intercept pixel which uses log=1).
      const redirectUrl = `${thirdPartyUrl}${thirdPartyUrl.includes('?') ? '&' : '?'}log=2`;
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    }

    onClose?.();
  }, [thirdPartyUrl, mobtk, onClose]);

  const handleExit = useCallback(() => {
    onClose?.();
  }, [onClose]);
  
  if (!isOpen) {
    return null;
  }

  return (
      <Modal
          visible={isOpen}
          transparent
          animationType="fade"
          onRequestClose={handleExit}
          statusBarTranslucent
      >
          <Box sx={styles.fullScreenContainer}>
              <Box sx={styles.header}>
                  <Image source={indeedLogo} style={styles.logo} accessible={false} />
              </Box>

              <Box sx={styles.content}>
                  <Image
                      source={successfulSignInImage}
                      style={styles.image}
                      accessible={false}
                  />
                  <Text
                      ref={titleRef}
                      sx={styles.title}
                      accessibilityRole="header"
                  >
                      {titleText}
                  </Text>

                  <Text sx={styles.text}>{successfulSigninText}</Text>

                  {thirdPartyUrl && (
                      <Pressable
                          onPress={handleRedirectToThirdPartySite}
                          accessibilityRole="link"
                          accessibilityLabel={continueButtonText}
                      >
                          <Text sx={styles.continueLink}>{continueButtonText}</Text>
                      </Pressable>
                  )}
              </Box>
          </Box>
      </Modal>
  );
};

const styles = StyleSheet.create({
  fullScreenContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  header: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
  },
  logo: {
    height: 30,
    width: 120,
    resizeMode: 'contain',
  },
  content: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  image: {
    width: '90%',
    maxWidth: 349,
    aspectRatio: 349 / 233,
    marginTop: 20,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#2d2d2d',
    textAlign: 'center',
    marginBottom: 8,
  },
  text: {
    fontSize: 16,
    color: '#595959',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  continueLink: {
    color: '#2d2d2d',
    fontSize: 16,
    fontWeight: '700',
  },
});
